import { useForm } from "react-hook-form";
import useFundingSources from "admin/hooks/useFundingSources";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import useToast from "~/components/ui/toast";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { trackEvent } from "~/utils/monitoring";

interface FundingSourceFormData {
  name: string;
  fundingAccountId: string;
}

interface CreateFundingSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateFundingSourceModal = ({ isOpen, onClose }: CreateFundingSourceModalProps) => {
  const { showSuccess, showError } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<FundingSourceFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      fundingAccountId: "",
    },
  });

  const { mutate } = useFundingSources(true);

  const onSubmit = async (data: FundingSourceFormData) => {
    trackEvent("admin_funding_source_create_clicked");
    const url = `${ACCESS_CONTROLLER_V1}/funding-sources`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name.trim(),
          funding_account_id: data.fundingAccountId.trim(),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const message =
          typeof (err?.message as { message?: string })?.message === "string"
            ? err.message.message
            : "Failed to create funding source, please try again.";
        showError(message);
        return;
      }
      showSuccess("Funding source created successfully");
      handleClose();
      mutate().catch(() => {});
    } catch (error) {
      showError("Failed to create funding source, please try again.");
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] bg-white flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 mb-4">
          <DialogTitle>New Funding Source</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-6 space-y-4 mb-4">
            <div className="space-y-1">
              <Label htmlFor="name" className="pb-1 gap-0">
                Funding Source Name<span className="text-destructive">*</span>
              </Label>
              <Input
                data-1p-ignore
                id="name"
                {...register("name", {
                  required: "Name is required",
                  minLength: { value: 1, message: "Name is required" },
                })}
                placeholder="e.g. NIH Grant R01"
                className={errors.name ? "border-destructive" : ""}
              />
              <div className="h-5">
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="fundingAccountId" className="pb-1 gap-0">
                Funding Id<span className="text-destructive">*</span>
              </Label>
              <Input
                data-1p-ignore
                id="fundingAccountId"
                {...register("fundingAccountId", {
                  required: "Id is required",
                  minLength: { value: 1, message: "Id is required" },
                })}
                placeholder="e.g. funding-account-123"
                className={errors.fundingAccountId ? "border-destructive" : ""}
              />
              <div className="h-5">
                {errors.fundingAccountId && (
                  <p className="text-sm text-destructive">{errors.fundingAccountId.message}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 px-6 py-4 border-t border-border">
            <Button type="button" variant="outline" onClick={handleClose} size="lg">
              Cancel
            </Button>
            <Button
              type="submit"
              size="lg"
              disabled={!isValid || isSubmitting}
              className="min-w-[96px]"
            >
              {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFundingSourceModal;
